import {EventEmitter} from '@angular/core';
import {HedgePositionsService} from "../../hedging-grid/positions-section/hedge-positions/hedge-positions.service";
import {LastQuoteCacheService} from "../../last-quote-cache.service";
import {MessageBusService} from "../../message-bus.service";
import {QuoteDto} from "../../shell-communication/dtos/quote-dto.class";
import {HedgePosition} from "../../hedging-grid/data-model/hedge-position";
import {Subscription} from "rxjs";
import {clone, jsonCloneObject, findHCF, isFuturesTicker, isValidNumber, isVoid, makePortfolioKey} from "../../utils";
import * as Enumerable from "linq";
import {ApgPortfolio} from "../../adjustment-pricing-grid/model/ApgPortfolio";
import {ServiceConfiguration} from "../../adjustment-pricing-grid/services/ServiceConfiguration";
import {ApgDataService} from "../../adjustment-pricing-grid/services/apg-data.service";
import {parseOptionTicker, zipOptionTicker} from "../../options-common/options.model";
import {isNullOrUndefined} from "util";

export interface HedgePriceChangedEventArgs {
    calls: number;
    puts: number;
    total: number;
    portfolioKey: string;
    delta: number;
}

interface HedgePositionsContainer {
    delta: number;
    positions: HedgePosition[];
    calls?: number;
    puts?: number;
    total?: number;
}

export class HedgesPricingService {

    constructor(
        private readonly _hedgePositionsService: HedgePositionsService,
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _messageBus: MessageBusService,
        private readonly _apgDataService: ApgDataService,
    ) {
    }

    private _subscriptions: Subscription[] = [];
    private _hedgePositionsByPortfolio: { [ix: string]: HedgePositionsContainer[] } = {};
    private _defaultQtyByPortfolio: { [ix: string]: number } = {};
    private _hedgeCostByGroup: { [ix: string]: number } = {};
    private _baseUnderlyingByPortfolio: { [ix: string]: string } = {};

    hedgePriceChanged$ = new EventEmitter<HedgePriceChangedEventArgs>();

    init() {

        this._hedgePositionsByPortfolio = {};
        this._hedgeCostByGroup = {};
        this._defaultQtyByPortfolio = {};
        this._baseUnderlyingByPortfolio = {};

        this._subscriptions.forEach((subscription: Subscription) => {
            subscription.unsubscribe();
        });
        this._subscriptions = [];

        this._subscriptions.push(
            this._messageBus.of<QuoteDto[]>('QuoteDto')
                .subscribe(x => this.onQuote(x.payload))
        );

        this._subscriptions.push(
            this._messageBus.of<{ portfolio: ApgPortfolio, positions: HedgePosition[] }>('Hg.InitialPositionsChanged')
                .subscribe(x => this.onHedgePositionsChanged(x.payload))
        );

        this._subscriptions.push(
            this._messageBus.of<{ portfolio: ApgPortfolio, oldPositions?: HedgePosition[], isReload?: boolean }>('Hg.HedgesCleared')
                .subscribe(x => this.onHedgesCleared(x.payload))
        );
    }

    async subscribe(portfolio: ApgPortfolio, delta = 0) {

        if (isVoid(portfolio)) {
            return;
        }

        const key = `${portfolio.userId}@${portfolio.id}`;

        let containers: HedgePositionsContainer[];

        if (key in this._hedgePositionsByPortfolio) {

            containers = this._hedgePositionsByPortfolio[key];

            if (containers.length > 0) {
                const existingContainer = containers.find(x => x.delta === delta);
                if (!isVoid(existingContainer)) {
                    // Re-select of an already-known portfolio: re-read the saved book instead of
                    // serving the cached one. The cache was the reason a portfolio reload could not
                    // recover from a stale (e.g. wiped) book — nothing on the reload path publishes
                    // Hg.InitialPositionsChanged, so the cached container never caught up.
                    // calculateTotals() emits hedgePriceChanged$ for the consumers that used to rely
                    // on the deferred emit here.
                    await this.refreshContainer(existingContainer, key, portfolio);
                    return;
                }
            }
        } else {
            containers = [];
            this._hedgePositionsByPortfolio[key] = containers;
        }

        let newContainer: HedgePositionsContainer = {delta, positions: []};
        containers.push(newContainer);

        let positions = await this._hedgePositionsService.getHedgePositions(portfolio);

        positions = jsonCloneObject(positions);

        if (delta !== 0) {
            positions.forEach(pos => {
                const tickerObj = parseOptionTicker(pos.ticker);
                tickerObj.strike = tickerObj.strike - delta;
                pos.ticker = zipOptionTicker(tickerObj);
                pos.strike = tickerObj.strike;
            });
        }

        const portfolioDefaultQty = await this._apgDataService.getDefaultQtyForPortfolio(portfolio)

        this._defaultQtyByPortfolio[key] = portfolioDefaultQty;

        const baseUnderlying = await this._apgDataService.getUnderlyingOfPortfolio(portfolio);
        this._baseUnderlyingByPortfolio[key] = baseUnderlying;

        this.changeMarketDataSubscription(positions, key);

        newContainer.positions = positions;

        // changeMarketDataSubscription() above priced the NEW positions from the quote cache but
        // totalled the container's OLD (empty) list; total the assigned list now so the first
        // read does not have to wait for the next quote tick.
        this.calculateTotals(key);
    }

    /**
     * Re-reads the saved hedge book of `portfolio` into one container (shifted by the container's
     * delta), diffs the quote subscriptions and re-totals. Used when the book was re-read from source
     * elsewhere (Hedge Pad reload) or the portfolio is re-selected.
     */
    private async refreshContainer(container: HedgePositionsContainer, key: string, portfolio: ApgPortfolio) {

        let positions = await this._hedgePositionsService.getHedgePositions(portfolio);

        positions = jsonCloneObject(positions);

        if (container.delta !== 0) {
            positions.forEach(pos => {
                const tickerObj = parseOptionTicker(pos.ticker);
                tickerObj.strike = tickerObj.strike - container.delta;
                pos.ticker = zipOptionTicker(tickerObj);
                pos.strike = tickerObj.strike;
            });
        }

        this._defaultQtyByPortfolio[key] = await this._apgDataService.getDefaultQtyForPortfolio(portfolio);
        this._baseUnderlyingByPortfolio[key] = await this._apgDataService.getUnderlyingOfPortfolio(portfolio);

        const oldTickers = (container.positions || []).map(x => x.ticker);
        const newTickers = positions.map(x => x.ticker);
        this._lastQuoteCache.subscribeTickersDiff(oldTickers, newTickers);

        positions.forEach(hp => {
            const lastQuote = this._lastQuoteCache.getLastQuote(hp.ticker);
            if (isVoid(lastQuote)) {
                return;
            }
            hp.price = lastQuote.mid;
        });

        container.positions = positions;

        this.calculateTotals(key);
    }

    onQuote(payload: QuoteDto[]) {

        for (const portfolioKey in this._hedgePositionsByPortfolio) {

            const containers = this._hedgePositionsByPortfolio[portfolioKey];

            for (const container of containers) {

                const positions = container.positions;

                if (isVoid(positions)) {
                    continue;
                }

                positions.forEach(x => {
                    const aQuote = payload.find(q => q.ticker === x.ticker);
                    if (isVoid(aQuote)) {
                        return;
                    }
                    x.price = aQuote.mid;
                });

                this.calculateTotals(portfolioKey);
            }
        }
    }

    // CR-20260916-003: iterates the PORTFOLIO CONTAINERS, not the positions service. The loop
    // used to walk `this._hedgePositionsService`'s own property names, none of which is a
    // portfolio key, so changeMarketDataSubscription found no container and released nothing:
    // every consumer (APG Hedges Total, Hedge Matrix, Package Comparison, OPG, now Maven) kept its
    // ticker subscriptions after dispose. Found while wiring Maven; the owner ruled it fixed here.
    dispose() {
        for (const pfKey in this._hedgePositionsByPortfolio) {
            this.changeMarketDataSubscription([], pfKey);
        }

        this._subscriptions.forEach(x => x.unsubscribe());
        this._subscriptions = [];
    }

    getHedgesCost(key: string, type: "calls" | "puts" | "total", delta = 0, hedgeFilter?: string[]): number {

        const containers = this._hedgePositionsByPortfolio[key];

        if (isVoid(containers)) {
            return undefined;
        }

        const targetContainer = containers.find(x => x.delta === delta);

        if (isVoid(targetContainer)) {
            return undefined;
        }

        return targetContainer[type];
    }

    getHedgeGroupCost(groupId: string): number {
        const hedgeCost = this._hedgeCostByGroup[groupId] || 0;
        return hedgeCost;
    }

    // CR-20260916-003 (Maven's Close selection). The accessor above collapses "not priced yet"
    // into 0, which the existing consumers accept; a list that shows a price per hedge must show
    // a DASH for an unpriced one, not a zero. Additive: nothing else changes.
    getHedgeGroupCostOrNull(groupId: string): number | null {
        const cost = this._hedgeCostByGroup[groupId];
        return isValidNumber(cost) ? cost : null;
    }

    private changeMarketDataSubscription(newPositions: HedgePosition[], key: string) {

        const containers = this._hedgePositionsByPortfolio[key];

        if (isVoid(containers)) {
            return;
        }

        for (const container of containers) {

            const oldPositions = container.positions || [];

            const oldTickers = oldPositions.map(x => x.ticker);

            const freshPositions: HedgePosition[] = jsonCloneObject(newPositions);

            if (container.delta !== 0) {
                freshPositions.forEach(freshPosition => {
                    const tickerObj = parseOptionTicker(freshPosition.ticker);
                    tickerObj.strike = tickerObj.strike - container.delta;
                    freshPosition.ticker = zipOptionTicker(tickerObj);
                    freshPosition.strike = tickerObj.strike;
                });
            }

            const newTickers = newPositions.map(x => x.ticker);

            this._lastQuoteCache.subscribeTickersDiff(oldTickers, newTickers);

            newPositions.forEach(hp => {
                const lastQuote = this._lastQuoteCache.getLastQuote(hp.ticker);
                if (isVoid(lastQuote)) {
                    return;
                }
                hp.price = lastQuote.mid;
            });

        }

        this.calculateTotals(key);

    }

    private calculateTotals(portfolioId: string) {

        const containers = this._hedgePositionsByPortfolio[portfolioId];

        for (let container of containers) {

            let calls = undefined;
            let puts = undefined;

            if (isVoid(container)) {
                return;
            }

            const positions = container.positions || [];

            const baseQty = this._defaultQtyByPortfolio[portfolioId] || 1;

            Enumerable.from(positions)
                .groupBy(x => x.type)
                .forEach(grp => {

                    const hedgeGroups = grp.groupBy(x => x.groupId);

                    hedgeGroups.forEach(hg => {
                        const qtties = hg.select(pos => Math.abs(pos.qty)).toArray();

                        const hcf = findHCF(qtties);

                        let prices = hg.select(pos => {
                            let px = isValidNumber(pos.price)
                                ? pos.price * pos.qty
                                : null;
                            return px;
                        });

                        let cost = null;
                        if (prices.all(price => isValidNumber(price))) {
                            cost = prices.sum();
                        }

                        cost = cost / hcf;

                        const ratio = hcf / baseQty;

                        if (isValidNumber(ratio, true)) {
                            cost = cost * ratio;
                        }

                        // Normalize non-base underlyings (ES=$50/point vs SPX/SPY=$100/point)
                        const asset = hg.first()?.asset;
                        const baseUl = this._baseUnderlyingByPortfolio[portfolioId];
                        if (asset && asset !== baseUl && isFuturesTicker(asset)) {
                            cost = cost * 0.5;
                        }

                        this._hedgeCostByGroup[hg.key()] = cost;

                        if (isValidNumber(cost)) {
                            if (grp.key() === 'Call') {
                                calls = (calls || 0) + cost;
                            } else if (grp.key() === 'Put') {
                                puts = (puts || 0) + cost;
                            }
                        }
                    });
                });

            let total: number;

            if (isValidNumber(calls) || isValidNumber(puts)) {
                total = (calls || 0) + (puts || 0);
            } else {
                total = undefined;
            }

            container.calls = calls;
            container.puts = puts;
            container.total = total;

            this.hedgePriceChanged$.emit({calls, puts, total, portfolioKey: portfolioId, delta: container.delta});
        }
    }

    private onHedgePositionsChanged(payload: { portfolio: ApgPortfolio, positions: HedgePosition[] }) {

        const pfKey = makePortfolioKey(payload.portfolio);

        const containers = this._hedgePositionsByPortfolio[pfKey];

        if (isNullOrUndefined(containers)) {
            return;
        }

        if (isVoid(containers)) {
            containers.push({delta: 0, positions: []});
        }

        for (const container of containers) {

            this.changeMarketDataSubscription(payload?.positions || [], pfKey);

            container.positions = payload.positions;

            this.calculateTotals(pfKey);
        }
    }

    private async onHedgesCleared(payload: { portfolio: ApgPortfolio, oldPositions?: HedgePosition[], isReload?: boolean }) {

        // isReload: the Hedge Pad re-reads the saved book (Reload Hedges / after Load Defaults) and
        // announces it on this topic with NO oldPositions. The book is NOT empty — it was just
        // (re)written. Treating it as a wipe emptied this service's containers right after
        // Hg.InitialPositionsChanged had filled them: quotes then updated nothing and every hedge
        // group cost froze at whatever the first 50 ms had produced (zero on a fresh login).
        // The Hedge Pad's own listener already skips isReload; this one re-reads the book instead.
        if (payload?.isReload) {
            const pfKey = makePortfolioKey(payload.portfolio);
            const containers = this._hedgePositionsByPortfolio[pfKey];
            if (isNullOrUndefined(containers)) {
                return;
            }
            for (const container of containers) {
                await this.refreshContainer(container, pfKey, payload.portfolio);
            }
            return;
        }

        Enumerable.from(payload.oldPositions).select(x => x.groupId)
            .distinct()
            .forEach((x, i) => {
                delete this._hedgeCostByGroup[x];
            });

        this.onHedgePositionsChanged({portfolio: payload.portfolio, positions: []});
    }

    getHedgesCostPicked(visible: string[]) {
        const costs = visible.map(x => this._hedgeCostByGroup[x] || 0);
        const total = costs.reduce((p, c) => p + c, 0);
        return total;
    }
}